import { v4 } from "uuid";
import { TOURNAMENT_GAME_RESULT } from "../constants";
import { TournamentError } from "../errors";
import { addDuration, endOf, getDate, isAfter, isAfterOrEqual, isBeforeOrEqual, setDateOfMonth, setDayOfWeek, startOf, toNativeDate, } from "../utils/date-helper";
import { getCollection, getDocument } from "../utils/firebase-helper";
import { randomGameResult } from "../utils/game-helper";
import { hasCommencedByMoveCount } from "../utils/match-helper";
import { getMatchups, getStandings } from "./manager";
import { sanitizePlayers } from "./sanitizer";
const BYE_RESULTS = [
    TOURNAMENT_GAME_RESULT.PAIRINGBYE,
    TOURNAMENT_GAME_RESULT.FULLBYE,
    TOURNAMENT_GAME_RESULT.HALFBYE,
    TOURNAMENT_GAME_RESULT.ZEROBYE,
];
export const isByeGame = (game) => {
    return (BYE_RESULTS.includes(game.whiteResult) ||
        BYE_RESULTS.includes(game.blackResult));
};
export const initBoards = (roundNumber, boardCount) => {
    return Array(boardCount)
        .fill(undefined)
        .map((_, i) => newBoard(roundNumber, i + 1));
};
export const newBoard = (roundNumber, boardNumber) => {
    return {
        gameId: `${("000" + roundNumber).slice(-2)}_${("000" + boardNumber).slice(-4)}`,
        roundNumber: roundNumber,
        board: boardNumber,
        matchGUID: v4(),
    };
};
export const getPlayerReward = (rank, reward, rewardParticipation) => {
    if (!reward || !rank)
        return null;
    if (rank === 1)
        return `${reward}|1`;
    else if (rank === 2)
        return `${reward}|2`;
    else if (rank === 3)
        return `${reward}|3`;
    else if (rewardParticipation)
        return "Participation";
    return null;
};
export const getRewardPoints = (reward) => {
    switch (reward) {
        case "Trophy|1":
            return 1000000;
        case "Trophy|2":
            return 100000;
        case "Trophy|3":
            return 10000;
        case "Medal|1":
            return 1000;
        case "Medal|2":
            return 100;
        case "Medal|3":
            return 10;
        case "Participation":
            return 1;
        default:
            return 0;
    }
};
export const hasResultMismatch = (game, match, moves) => {
    if (!game.matchRef)
        return false;
    const { whiteResult, blackResult } = getTournamentResultsFromMatch(match, moves);
    if (whiteResult === game.whiteResult && blackResult === game.blackResult) {
        return false;
    }
    console.log(`game ${game.gameId} hasResultMismatch ${whiteResult}-${blackResult} vs ${game.whiteResult}-${game.blackResult}`);
    return true;
};
export const getTournamentResultsFromMatch = function (match, moves) {
    let whiteResult = TOURNAMENT_GAME_RESULT.UNFINISHED, blackResult = TOURNAMENT_GAME_RESULT.UNFINISHED;
    if (!match || !match.result) {
        if (!hasCommencedByMoveCount(match, moves)) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        }
    }
    else if (match.result === "forfeit") {
        if (match.player_1_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITW;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        }
        else if (match.player_2_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITW;
        }
        else if (match.player_1_score === 0.5) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITD;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITD;
        }
        else if (match.player_1_score === 0 && match.player_2_score === 0) {
            whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
            blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        }
    }
    else if (match.result === "abort") {
        whiteResult = TOURNAMENT_GAME_RESULT.FORFEITL;
        blackResult = TOURNAMENT_GAME_RESULT.FORFEITL;
    }
    else {
        if (match.player_1_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.WIN;
            blackResult = TOURNAMENT_GAME_RESULT.LOSS;
        }
        else if (match.player_2_score === 1) {
            whiteResult = TOURNAMENT_GAME_RESULT.LOSS;
            blackResult = TOURNAMENT_GAME_RESULT.WIN;
        }
        else if (match.player_1_score === 0.5) {
            whiteResult = TOURNAMENT_GAME_RESULT.DRAW;
            blackResult = TOURNAMENT_GAME_RESULT.DRAW;
        }
        else {
            whiteResult = TOURNAMENT_GAME_RESULT.LOSS;
            blackResult = TOURNAMENT_GAME_RESULT.LOSS;
        }
    }
    return { whiteResult: whiteResult, blackResult: blackResult };
};
export const finishTournamentGame = async (firestore, match, moves) => {
    if (!match)
        return;
    if (!match.result)
        return;
    if (!match.tournamentId)
        return;
    const { whiteResult, blackResult } = getTournamentResultsFromMatch(match, moves);
    const { gameId } = newBoard(match.roundNumber, match.board);
    const tournamentRef = firestore
        .collection("tournaments")
        .doc(match.tournamentId);
    const tournamentGameSnapshot = await tournamentRef
        .collection("games")
        .doc(gameId)
        .get();
    const tournamentGame = tournamentGameSnapshot.data();
    if (!tournamentGame)
        return;
    if (tournamentGame.whiteResult === whiteResult &&
        tournamentGame.blackResult === blackResult &&
        tournamentGame.startTime &&
        tournamentGame.finishTime) {
        return; // match has already finialized
    }
    console.log("finishTournamentGame", match.id, match.result, {
        whiteResult,
        blackResult,
    });
    await tournamentRef.collection("games").doc(tournamentGame.gameId).update({
        startTime: match.startedAt,
        finishTime: match.endedAt,
        whiteResult: whiteResult,
        blackResult: blackResult,
    });
};
// SETUP TOURNAMENT
export const hasPlayer = async (firestore, tournament, player) => {
    const doc = await firestore
        .collection("tournaments")
        .doc(tournament.id)
        .collection("players")
        .doc(player.username)
        .get();
    return doc.exists;
};
export const addPlayer = async (firestore, player, tournament) => {
    const tournamentRef = firestore.collection("tournaments").doc(tournament.id);
    const playersRef = tournamentRef.collection("players");
    const players = await getCollection(playersRef);
    const playerExists = players
        ? !!players.find((p1) => p1.username === player.username)
        : await hasPlayer(firestore, tournament, player);
    const playerRef = playersRef.doc(player.username);
    if (playerExists) {
        console.log("update", player.username, "in tournament");
        await playerRef.update({
            user: player,
            deletedAt: null,
        });
    }
    else {
        console.log("add", player.username, "to tournament");
        await playerRef.set({
            username: player.username,
            score: 0,
            rating: 0,
            roundsParticipated: 0,
            user: player,
            deletedAt: null,
            startingRank: players.length + 1,
        }, { merge: false });
    }
    const playerUsernames = tournament.playerUsernames
        ? [...new Set([...tournament.playerUsernames, player.username])]
        : [player.username];
    await tournamentRef.update({
        playerCount: playerUsernames.length,
        playerUsernames: playerUsernames,
    });
};
export const removePlayer = async (firestore, player, tournament) => {
    const tournamentRef = firestore.collection("tournaments").doc(tournament.id);
    const playersRef = tournamentRef.collection("players");
    const players = await getCollection(playersRef);
    const playerExists = players
        ? !!players.find((p1) => p1.username === player.username)
        : await hasPlayer(firestore, tournament, player);
    if (!playerExists)
        return;
    console.log("remove", player.username, "from tournament");
    const playerRef = playersRef.doc(player.username);
    await playerRef.update({
        deletedAt: new Date(),
    });
    const index = tournament.playerUsernames.indexOf(player.username);
    if (index !== -1) {
        const playerUsernames = [...tournament.playerUsernames];
        playerUsernames.splice(index, 1);
        await tournamentRef.update({
            playerCount: playerUsernames.length,
            playerUsernames: playerUsernames,
        });
    }
};
// get next occurrence after a specific time
// in case of weekly, you can set on all days of week from Mon-Sun
// hence it could return multiple next occurrences
const getNextOccurrence = (startTime, settings) => {
    const { every, basis, on } = settings.repeat;
    const { monthAbsolute, monthRelative, monthType, weekdays } = on;
    const next = addDuration(startTime, every, basis);
    let nextPossibleDates = [];
    if (basis === "minute" || basis === "day") {
        nextPossibleDates.push(next);
    }
    else if (basis === "week") {
        let date = startOf(next, "week");
        const endOfWeek = endOf(next, "week");
        const dates = [];
        while (isBeforeOrEqual(date, endOfWeek)) {
            if (isAfter(date, startTime)) {
                dates.push(date);
            }
            date = addDuration(date, 1, "day");
        }
        nextPossibleDates = nextPossibleDates.concat(dates.filter((d) => weekdays.includes(d.day().toString())));
    }
    else if (basis === "month") {
        if (monthType === "absolute") {
            const date = setDateOfMonth(next, monthAbsolute);
            if (isAfter(date, startTime)) {
                nextPossibleDates.push(date);
            }
        }
        else {
            const startOfMonth = startOf(next, "month");
            // 0 -> 7
            // 1 -> 14
            // 2 -> 21
            // 3 -> 28
            const baseCount = (Number(monthRelative.which) + 1) * 7;
            let monthDate = setDayOfWeek(startOfMonth, Number(monthRelative.weekday) + baseCount);
            if (getDate(monthDate) > baseCount) {
                monthDate = addDuration(monthDate, -7, "day");
            }
            if (isAfter(monthDate, startTime)) {
                nextPossibleDates.push(monthDate);
            }
        }
    }
    return nextPossibleDates;
};
/**
 * This function returns the exact dates of the coming occurrences starting from settings.createdTime
 * @param startTime start time of the current occurrence.
 * Add multiples of the time increment basis to calculate next occurrences's start time
 * @param currentOccurrence the current occurrence number starting from 1.
 * Should not be greater than settings.maxOccurrences
 * @param settings recurrence settings
 * @returns an array of coming occurrences's start time
 * All time must be in the future
 * If settings.ends.maxOccurrences > 0 then the length should be equal to maxOccurrences - currentOccurrence
 * If settings.ends.onDate then all times must be before ends.onDate
 * If settings.ends.type = never, return 120 next occurrences
 */
export const getNextOccurrences = (startTime, currentOccurrence, settings) => {
    if (!startTime || !currentOccurrence) {
        return [];
    }
    const { repeat, ends } = settings;
    if (!repeat || !ends) {
        return [];
    }
    const { every, basis } = repeat;
    if (!every || !basis) {
        return [];
    }
    let nextPossibleDates = [];
    const beginTime = toNativeDate(settings.createdTime);
    let next = toNativeDate(startTime);
    if (ends.type === "date") {
        const endDate = endOf(new Date(ends.onDate), "day");
        while (isBeforeOrEqual(next, endDate)) {
            const dates = getNextOccurrence(next, settings);
            nextPossibleDates = [...nextPossibleDates, ...dates];
            next = addDuration(next, every, basis);
        }
        nextPossibleDates = nextPossibleDates.filter((d) => isAfterOrEqual(d, beginTime));
    }
    else {
        // ens after maxOccurrences or
        // when ends never -> generate 120 next occurrences
        // to make sure even with duration = 1 minute
        // there is at least some next occurrences
        const maxOccurrences = ends.type === "occurrences" ? ends.maxOccurrences : 10;
        while (nextPossibleDates.length < maxOccurrences) {
            const dates = getNextOccurrence(next, settings);
            nextPossibleDates = [
                ...nextPossibleDates,
                ...dates.filter((d) => isAfterOrEqual(d, beginTime)),
            ];
            next = addDuration(next, every, basis);
        }
        nextPossibleDates = nextPossibleDates.slice(0, ends.maxOccurrences - currentOccurrence);
    }
    return nextPossibleDates.filter((d) => isAfter(d, new Date()));
};
// SETUP ROUND
export const scheduleRounds = async (firestore, tournament, rounds) => {
    if (tournament.currentRound > tournament.totalRounds)
        return;
    const sortedRounds = [...rounds].sort((a, b) => a.roundNumber - b.roundNumber);
    const roundInterval = tournament.baseTimeControl * 2 + 2;
    const currentRound = tournament.currentRound
        ? sortedRounds[tournament.currentRound - 1]
        : null;
    // find starting time of remaining rounds
    let startTime = null;
    if (!currentRound) {
        startTime = tournament.startTime.seconds;
    }
    else if (currentRound.finishTime) {
        startTime = currentRound.finishTime.seconds;
    }
    else if (currentRound.startTime) {
        startTime =
            tournament.startTime.seconds + 60 * (2 * tournament.baseTimeControl + 2);
    }
    if (!startTime || startTime < Date.now() / 1000) {
        startTime = Date.now() / 1000 + 60;
    }
    // schedule start time of each round
    for (let roundNumber = tournament.currentRound + 1; roundNumber <= tournament.totalRounds; ++roundNumber) {
        const roundStartTime = new Date(1000 *
            (startTime +
                60 * roundInterval * (roundNumber - tournament.currentRound - 1)) +
            1);
        await setRoundStartTime(firestore, tournament, sortedRounds, roundNumber, roundStartTime);
    }
};
export const setRoundStartTime = async (firestore, tournament, rounds, roundNumber, date) => {
    console.log("reschedule round", roundNumber, "round start at", date);
    if (date < tournament.startTime.toDate())
        return;
    console.log("reschedule round", roundNumber, "round start time > tournament start time");
    if (roundNumber > 1 &&
        rounds[roundNumber - 2] &&
        date < rounds[roundNumber - 2].startTime.toDate()) {
        return;
    }
    console.log("reschedule round", roundNumber, "round start time > previous round start time");
    const tournamentRef = firestore.collection("tournaments").doc(tournament.id);
    const roundsRef = tournamentRef.collection("rounds");
    const round = rounds.find((r) => r.roundNumber === roundNumber);
    if (round) {
        await roundsRef.doc(round.id).update({
            startTime: date,
        });
    }
    else {
        await roundsRef.doc(roundNumber + "").set({
            roundNumber: roundNumber,
            startTime: date,
        });
    }
};
export const pushRounds = async (firestore, tournament, rounds, roundNumber, minutes) => {
    const tournamentRef = firestore.collection("tournaments").doc(tournament.id);
    for (let i = roundNumber; i <= tournament.totalRounds; ++i) {
        if (i < tournament.currentRound)
            continue;
        const round = rounds.find((r) => r.roundNumber === i);
        if (!round)
            continue;
        const startTime = new Date(1000 * (round.startTime.seconds + minutes * 60));
        if (i === 1) {
            await tournamentRef.update({
                startTime: startTime,
            });
        }
        await setRoundStartTime(firestore, tournament, rounds, i, startTime);
    }
};
// STEP 1: PREPARE ROUND
export const isPlayerInRound = (player, roundNumber) => {
    if (!roundNumber)
        roundNumber = 1;
    const mask = 1 << (roundNumber - 1);
    return !!(player.roundsParticipated & mask);
};
export const getPlayedColors = (player) => {
    return player.matches
        .filter((m) => !m.bye)
        .map((m) => (m.seating === 1 ? "w" : "b"));
};
export const getColorPrefNumber = (player) => {
    const colors = getPlayedColors(player);
    const colorPrefNumber = colors.reduce((value, color) => {
        return color === "w" ? value + 1 : value - 1;
    }, 0);
    return colorPrefNumber;
};
export const getRoundPlayers = async (firestore, tournament, players, games, round) => {
    const roundPlayers = players.map((p) => {
        const playedGames = games
            .filter((g) => (g.whiteUsername === p.username || g.blackUsername === p.username) &&
            g.roundNumber < round.roundNumber)
            .map((g) => {
            // eslint-disable-next-line
            const { id, ...rest } = g;
            return rest;
        });
        // eslint-disable-next-line
        const { id, ...rest } = p;
        return {
            ...rest,
            seed: p.user?.stats?.rating ?? 0,
            isParticipating: isPlayerInRound(p, round.roundNumber),
            active: !p.deletedAt,
            scoreBeforeGame: getScoreBeforeGame(p.username, round),
            games: playedGames,
        };
    });
    await sanitizePlayers(firestore, tournament, roundPlayers);
    return roundPlayers;
};
export const setGamePlayers = (game, whiteUsername, blackUsername) => {
    if (whiteUsername) {
        game.whiteUsername = whiteUsername;
        game.whiteResult = blackUsername
            ? TOURNAMENT_GAME_RESULT.UNFINISHED
            : TOURNAMENT_GAME_RESULT.PAIRINGBYE;
    }
    else {
        game.whiteUsername = null;
        game.whiteResult = null;
    }
    if (blackUsername) {
        game.blackUsername = blackUsername;
        game.blackResult = whiteUsername
            ? TOURNAMENT_GAME_RESULT.UNFINISHED
            : TOURNAMENT_GAME_RESULT.PAIRINGBYE;
    }
    else {
        game.blackUsername = null;
        game.blackResult = null;
    }
    game.startTime = null;
    game.finishTime = null;
};
export const getScoreBeforeGame = (username, round) => {
    if (round.roundNumber === 1)
        return 0;
    if (!round.standings || !round.standings.length)
        return 0;
    const standingAtRound = round.standings.find((s) => (s.player?.id ?? s.id) === username);
    if (standingAtRound)
        return standingAtRound.wins;
    return 0;
};
export const shouldUpdateGame = (currentGame, updatedGame) => {
    return (updatedGame.whiteUsername !== currentGame.whiteUsername ||
        updatedGame.blackUsername !== currentGame.blackUsername ||
        updatedGame.whiteResult !== currentGame.whiteResult ||
        updatedGame.blackResult !== currentGame.blackResult ||
        updatedGame.whiteScoreBeforeGame !== currentGame.whiteScoreBeforeGame ||
        updatedGame.blackScoreBeforeGame !== currentGame.blackScoreBeforeGame ||
        (!currentGame.matchRef && updatedGame.matchRef) ||
        (currentGame.matchRef && currentGame.matchRef.id !== updatedGame.matchGUID));
};
export const saveRoundGame = async (firestore, tournamentId, round, game, games) => {
    const gamesRef = firestore
        .collection("tournaments")
        .doc(tournamentId)
        .collection("games");
    const matchRef = firestore.collection("matches").doc(game.matchGUID);
    const updatedGame = {
        ...game,
        matchRef: game.matchRef ? matchRef : null,
        whiteScoreBeforeGame: getScoreBeforeGame(game.whiteUsername, round),
        blackScoreBeforeGame: getScoreBeforeGame(game.blackUsername, round),
    };
    const currentGame = games.find((g) => g.gameId === game.gameId);
    if (!currentGame) {
        await gamesRef.doc(game.gameId).set(updatedGame);
        console.log("added round game", game.gameId);
    }
    else if (shouldUpdateGame(currentGame, updatedGame)) {
        await gamesRef.doc(game.gameId).update(updatedGame);
        console.log("updated round game", game.gameId);
    }
};
export const saveRound = async (firestore, tournamentId, round, roundGames, games) => {
    const tournamentRef = firestore.collection("tournaments").doc(tournamentId);
    const snapshot = await tournamentRef.get();
    const tournament = snapshot.data();
    if (tournament.currentRound <= 1) {
        await firestore
            .collection("tournaments")
            .doc(tournamentId)
            .update({
            currentRound: roundGames.some((g) => g.whiteUsername && g.blackUsername)
                ? 1
                : 0,
        });
    }
    for (const game of roundGames) {
        await saveRoundGame(firestore, tournamentId, round, game, games);
    }
    console.log("saved round", round.roundNumber);
};
export const pairRound = async (params, bbpService) => {
    const { players, tournament, games, round } = params;
    /* eslint-disable */
    const { id: rId, finishTime: rFinishTime, startTime: rStartTime, ...rRest } = round;
    const { id: tId, games: tGames, steps: tSteps, players: tPlayers, ...tRest } = tournament;
    const data = {
        tournament: tRest,
        games: games.map((g) => {
            const { id, finishTime, ...rest } = g;
            return rest;
        }),
        players: players.map((p) => {
            const { games: pGames, ...restP } = p;
            return {
                ...restP,
                games: pGames.map((g) => {
                    const { finishTime, ...rest } = g;
                    return rest;
                }),
            };
        }),
        round: rRest,
    };
    let matchups = await getMatchups({
        tournament: data.tournament,
        games: data.games,
        players: data.players,
        roundNumber: data.round.roundNumber,
    }, bbpService);
    // sort by score to display pair with total highest score first
    matchups.sort((a, b) => {
        const player11 = players.find((p) => p.username === a.whiteUsername);
        const player12 = players.find((p) => p.username === a.blackUsername);
        const player21 = players.find((p) => p.username === b.whiteUsername);
        const player22 = players.find((p) => p.username === b.blackUsername);
        const matchAScore = (player11?.scoreBeforeGame ?? 0) + (player12?.scoreBeforeGame ?? 0);
        const matchBScore = (player21?.scoreBeforeGame ?? 0) + (player22?.scoreBeforeGame ?? 0);
        return matchBScore - matchAScore;
    });
    console.log("matchups", JSON.stringify(matchups));
    return matchups;
};
// STEP 2: START ROUND
export const startRoundGame = async (firestore, serverTimestamp, tournament, game, players) => {
    if (game.matchRef)
        return true;
    if (!game.whiteUsername || !game.blackUsername)
        return;
    const whitePlayer = players.find((p) => p.username === game.whiteUsername);
    const blackPlayer = players.find((p) => p.username === game.blackUsername);
    if (!whitePlayer || !blackPlayer || !whitePlayer.user || !blackPlayer.user) {
        throw new TournamentError(tournament.id, game.roundNumber, "startRoundGame", `Couldn't find player ${!whitePlayer ? game.whiteUsername : game.blackUsername} in game ${game.gameId}`);
    }
    const gamesRef = firestore
        .collection("tournaments")
        .doc(tournament.id)
        .collection("games");
    const gameRef = gamesRef.doc(game.gameId);
    if (whitePlayer.user.aiLevel && blackPlayer.user.aiLevel) {
        await gameRef.update({
            finishTime: serverTimestamp,
            ...randomGameResult(),
        });
        return true;
    }
    console.log("startRoundGame", game.gameId);
    const matchRef = firestore.collection("matches").doc(game.matchGUID);
    const docSnapshot = await matchRef.get();
    if (!docSnapshot) {
        throw new TournamentError(tournament.id, game.roundNumber, "startRoundGame", `Error getting firestore match document ${game.matchGUID}`);
    }
    // Match already exists
    if (docSnapshot.exists) {
        console.log("Match already exists", docSnapshot.data());
    }
    await gameRef.update({
        startTime: serverTimestamp,
        matchRef: matchRef,
    });
};
// Create all round matches and set matchRef for each round game
export const startRoundGames = async (firestore, serverTimestamp, tournament, roundNumber, players, games) => {
    const roundGames = games.filter((g) => g.roundNumber == roundNumber &&
        !g.matchRef &&
        g.whiteUsername &&
        g.blackUsername);
    console.log("startRoundGames", roundGames.length);
    let started = 0;
    let failed = 0;
    let startedGames = [];
    let errors = [];
    for (const game of roundGames) {
        try {
            const result = await startRoundGame(firestore, serverTimestamp, tournament, game, players);
            if (result) {
                continue;
            }
            ++started;
            const whitePlayer = players.find((p) => p.username === game.whiteUsername);
            const blackPlayer = players.find((p) => p.username === game.blackUsername);
            startedGames.push({
                tournament,
                game,
                whitePlayer,
                blackPlayer,
            });
        }
        catch (err) {
            errors.push(err);
            ++failed;
        }
    }
    return { started, startedGames, failed, errors };
};
export const updateCurrentRound = (firestore, tournament, roundNumber) => {
    if (tournament.currentRound + 1 == roundNumber) {
        firestore
            .collection("tournaments")
            .doc(tournament.id)
            .update({ currentRound: roundNumber });
        console.log("updateCurrentRound to", roundNumber);
    }
};
// STEP 3: FINISH ROUND
export const finalizeRound = async (firestore, tournament, roundNumber, games) => {
    let mismatchedCount = 0;
    const roundGames = games.filter((g) => g.roundNumber == roundNumber);
    for (const game of roundGames) {
        if (!game.matchRef)
            continue;
        const match = (await getDocument(game.matchRef));
        console.log("finalizeRound game", game.id);
        if (!match)
            continue;
        const moves = (await getCollection(game.matchRef.collection("moves")));
        if (hasCommencedByMoveCount(match, moves) && !match.result) {
            // TODO: notify admin - match goes into overtime
            continue;
        }
        if (!hasResultMismatch(game, match, moves) && game.finishTime)
            continue;
        ++mismatchedCount;
        const { whiteResult, blackResult } = getTournamentResultsFromMatch(match, moves);
        console.log(`fix game ${game.id} result ${whiteResult}-${blackResult}`);
        const gamesRef = firestore
            .collection("tournaments")
            .doc(tournament.id)
            .collection("games");
        await gamesRef.doc(game.id).update({
            startTime: match.startedAt || null,
            finishTime: match.endedAt || null,
            whiteResult: whiteResult,
            blackResult: blackResult,
        });
    }
    return mismatchedCount;
};
export const saveStandings = async (firestore, tournament, roundNumber, rounds, players, games) => {
    if (!rounds || !rounds[roundNumber - 1]) {
        return;
    }
    console.log("saveStandings round", roundNumber);
    const roundPlayers = await getRoundPlayers(firestore, tournament, players, games, rounds[roundNumber - 1]);
    const standings = getStandings(tournament, games, roundPlayers);
    // Update round standingsAfter and standings before the next round if there is
    const roundsRef = firestore
        .collection("tournaments")
        .doc(tournament.id)
        .collection("rounds");
    await roundsRef.doc(roundNumber + "").update({
        standingsAfter: [...standings],
    });
    if (roundNumber < tournament.totalRounds) {
        await roundsRef.doc(`${roundNumber + 1}`).update({
            standings: standings,
        });
    }
    // Update players' rank of the current round
    let rank = 0;
    let lastStanding = null;
    if (roundNumber === tournament.currentRound ||
        roundNumber === tournament.totalRounds) {
        standings.forEach((standing, i) => {
            const rankIndex = i + 1;
            if (!lastStanding || standing.wins < lastStanding.wins) {
                lastStanding = standing;
                rank = rankIndex;
            }
            const myRank = rank;
            let reward = null;
            if (roundNumber === tournament.totalRounds && standing.matches) {
                reward = getPlayerReward(myRank, tournament.reward, tournament.rewardParticipation);
                if (reward)
                    console.log(standing.player?.id ?? standing.id, rankIndex, myRank, reward);
            }
            return firestore
                .collection("tournaments")
                .doc(tournament.id)
                .collection("players")
                .doc(standing.player?.id ?? standing.id)
                .update({
                score: standing.wins,
                rankIndex: rankIndex,
                rank: myRank,
                rankGroupCount: null,
                reward: reward,
            });
        });
    }
};
export const toggleRoundsParticipated = async (firestore, player, tournament, roundNumber = null, updatedUser = null) => {
    const toRoundNumber = !roundNumber || roundNumber >= tournament.currentRound
        ? tournament.totalRounds
        : roundNumber;
    if (!roundNumber) {
        roundNumber = Math.min(tournament.currentRound + 1, tournament.totalRounds);
    }
    const mask = (1 << toRoundNumber) - (1 << (roundNumber - 1));
    const updatedObject = {
        roundsParticipated: isPlayerInRound(player, roundNumber)
            ? player.roundsParticipated & ~mask
            : player.roundsParticipated | mask,
    };
    if (updatedUser)
        updatedObject.user = updatedUser;
    console.log("toggleRoundsParticipated", player.username, {
        roundNumber,
        toRoundNumber,
        mask,
        updatedObject,
    });
    await firestore
        .collection("tournaments")
        .doc(tournament.id)
        .collection("players")
        .doc(player.username)
        .update(updatedObject);
};
export const participate = (firestore, player, tournament, roundNumber = null, updatedUser = null) => {
    if (roundNumber == null)
        roundNumber = tournament.currentRound;
    if (isPlayerInRound(player, roundNumber))
        return;
    toggleRoundsParticipated(firestore, player, tournament, roundNumber, updatedUser);
};
export const unparticipate = async (firestore, player, tournament, roundNumber = null) => {
    if (roundNumber == null)
        roundNumber = tournament.currentRound;
    console.log(`${player.username} in round ${roundNumber}:`, isPlayerInRound(player, roundNumber));
    if (!isPlayerInRound(player, roundNumber))
        return;
    await toggleRoundsParticipated(firestore, player, tournament, roundNumber);
};
export const unparticipateForfeitedPlayers = async (firestore, tournament, roundNumber, players, games) => {
    const roundGames = games.filter((g) => g.roundNumber == roundNumber);
    const forfeitedPlayers = [];
    roundGames.forEach((g) => {
        if (g.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITL &&
            g.whiteUsername &&
            !g.whiteUsername.startsWith("ai_")) {
            forfeitedPlayers.push(g.whiteUsername);
        }
        if (g.blackResult === TOURNAMENT_GAME_RESULT.FORFEITL &&
            g.blackUsername &&
            !g.blackUsername.startsWith("ai_")) {
            forfeitedPlayers.push(g.blackUsername);
        }
    });
    console.log("unparticipateForfeitedPlayers", forfeitedPlayers);
    for (let i = 0; i < forfeitedPlayers.length; i++) {
        const username = forfeitedPlayers[i];
        const player = players.find((p) => p.username === username);
        if (!player)
            continue;
        await unparticipate(firestore, player, tournament, roundNumber);
    }
    return forfeitedPlayers;
};
export const finishRound = async (firestore, serverTimestamp, tournament, round, rounds, players, games) => {
    let roundNumber = round.roundNumber;
    const logs = [];
    const m = await finalizeRound(firestore, tournament, roundNumber, games);
    logs.push(`Resolve ${m} game-results`);
    // get the updated list of games
    if (m > 0) {
        const gamesRef = firestore
            .collection("tournaments")
            .doc(tournament.id)
            .collection("games");
        games = await getCollection(gamesRef);
    }
    // if there are live games with no results, add to logs and return
    const liveGames = games.filter((g) => g.roundNumber == roundNumber && g.matchRef && !g.finishTime);
    if (liveGames.length) {
        logs.push(`${liveGames.length} live games left`);
        return { status: "scheduled", logs: logs, matchCount: liveGames.length };
    }
    await saveStandings(firestore, tournament, roundNumber, rounds, players, games);
    logs.push("Save standings");
    if (!round.finishTime) {
        await firestore
            .collection("tournaments")
            .doc(tournament.id)
            .collection("rounds")
            .doc(roundNumber + "")
            .update({
            finishTime: serverTimestamp,
        });
    }
    logs.push(`Finish round ${roundNumber}`);
    if (tournament.currentRound === tournament.totalRounds) {
        // Finish tournament
        await firestore
            .collection("tournaments")
            .doc(tournament.id)
            .update({
            currentRound: tournament.totalRounds + 1,
            automate: false,
        });
        logs.push(`Set currentRound = ${tournament.totalRounds + 1}`);
        logs.push("End automation");
    }
    else {
        // unparticipate forfeit Loss players
        const forfeitedPlayers = await unparticipateForfeitedPlayers(firestore, tournament, roundNumber, players, games);
        logs.push(`Unparticipate ${forfeitedPlayers.length} forfeit Loss players`);
    }
    return { status: "finished", logs: logs, matchCount: 0 };
};
